import React, { useMemo, useState, useRef, useEffect } from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { getComprehensivePricingData } from "../services/pricingApi";
import { getEnrichedEvents } from "../services/geoeconomicAiApi";
import { getFriendShoringMatrix, getCountryIntelligence } from "../services/friendShoringApi";

// Global Resource Sentinel
// Path: frontend/src/pages/GlobalResourceSentinel.jsx
// Theme: Midnight Slate & Cyber Teal
//
// Geoeconomic Formulas (from spec):
// - Alignment Score = (V×0.3) + (T×0.3) + (D×0.4) - (S×0.5)  [V=UN voting, T=trade, D=defense, S=sanctions]
// - Dependence = Imports from Country X / Total Global Imports of Mineral Y
// - Days Remaining = Inventory / (Daily Consumption - (Daily Inflow × (1 - Shock Factor)))
// - Forecast Price_Geo = Base Price × (1 + (GPR × β))

// Quadrant logic: Safe (Green) | Concern (Yellow) | Critical Danger (Red)
function getQuadrantZone(alignment, dependence) {
  if (alignment > 70 && dependence < 30) return "safe";      // Safe Zone
  if (alignment < 40 && dependence > 50) return "critical";   // Critical Danger Zone
  if (alignment < 50 && dependence < 30) return "concern";     // Concern Zone
  return "transactional"; // Other
}

// GPR = 0.20 (20% high tension), β = 0.8 for Lithium
// Forecast_Geo = Base × (1 + (GPR × β)) = Base × 1.16
const GPR = 0.2;
const LITHIUM_BETA = 0.8;

// Choke points: lat, lng (for Globe Points/Rings). throughputLabel = "Current Throughput: X"
const CHOKE_POINTS = [
  { id: "malacca", name: "Strait of Malacca", lat: 1.5, lng: 102.5, throughputLabel: "40% of India's Lithium Inflow" },
  { id: "suez", name: "Suez Canal", lat: 30.7, lng: 32.3, throughputLabel: "12% of European transit for minerals" },
  { id: "hormuz", name: "Strait of Hormuz", lat: 26.57, lng: 56.25, throughputLabel: "8% of global refined shipments" },
];

// Tooltip HTML for choke point hover
const chokePointLabel = (d) =>
  `<div style="text-align:left;padding:6px;min-width:160px">
    <strong style="color:#ffffff;font-size:12px">${d.name}</strong><br/>
    <span style="color:#94a3b8;font-size:11px">Current Throughput: ${d.throughputLabel}</span>
  </div>`;

// India hub (Mumbai)
const INDIA = { lat: 18.94, lng: 72.83 };
const CHOKE_POINT_BY_ID = CHOKE_POINTS.reduce((acc, point) => {
  acc[point.id] = point;
  return acc;
}, {});

// Arcs: point-to-point routes (India → partner). type: "friend" | "risk" | "transactional"
const TRADE_ARCS = [
  { id: "ind-aus", startLat: INDIA.lat, startLng: INDIA.lng, endLat: -31.95, endLng: 115.86, label: "India → Australia", type: "friend" },
  { id: "ind-chl", startLat: INDIA.lat, startLng: INDIA.lng, endLat: -23.65, endLng: -70.4, label: "India → Chile", type: "friend" },
  { id: "ind-chn", startLat: INDIA.lat, startLng: INDIA.lng, endLat: 31.23, endLng: 121.47, label: "India → China", type: "risk" },
  { id: "ind-drc", startLat: INDIA.lat, startLng: INDIA.lng, endLat: -11.67, endLng: 27.48, label: "India → DRC", type: "risk" },
  { id: "ind-idn", startLat: INDIA.lat, startLng: INDIA.lng, endLat: -6.21, endLng: 106.85, label: "India → Indonesia", type: "transactional" },
];

// Paths: multi-segment routes via choke points. coords: [[lng, lat], ...] (GeoJSON order)
const TRADE_PATHS = [
  {
    id: "india-malacca-china",
    coords: [
      [INDIA.lng, INDIA.lat],
      [CHOKE_POINT_BY_ID.malacca.lng, CHOKE_POINT_BY_ID.malacca.lat],
      [121.47, 31.23],
    ],
    label: "India → Malacca → China",
    type: "risk",
  },
  {
    id: "hormuz-india",
    coords: [
      [CHOKE_POINT_BY_ID.hormuz.lng, CHOKE_POINT_BY_ID.hormuz.lat],
      [INDIA.lng, INDIA.lat],
    ],
    label: "Hormuz → India",
    type: "friend",
  },
  {
    id: "suez-india",
    coords: [
      [CHOKE_POINT_BY_ID.suez.lng, CHOKE_POINT_BY_ID.suez.lat],
      [INDIA.lng, INDIA.lat],
    ],
    label: "Suez Canal → India",
    type: "friend",
  },
];

export default function GlobalResourceSentinel() {
  const globeFallbackRef = useRef();
  const globeContainerRef = useRef();
  const [isDarkMode, setIsDarkMode] = useState(() =>
    typeof document !== "undefined" ? document.documentElement.classList.contains("dark") : true
  );
  const [globeSize, setGlobeSize] = useState({ width: 800, height: 600 });
  const [selectedCountry, setSelectedCountry] = useState("");
  const [chinaBan, setChinaBan] = useState(false);
  const [malaccaBlockade, setMalaccaBlockade] = useState(false);
  const [hormuzBlockade, setHormuzBlockade] = useState(false);
  const [southChinaSeaBlockade, setSouthChinaSeaBlockade] = useState(false);
  const [southChinaSeaPct, setSouthChinaSeaPct] = useState(0);
  const [priceInESG, setPriceInESG] = useState(false);
  const [selectedMineral, setSelectedMineral] = useState("Lithium");
  const [mineralPrices, setMineralPrices] = useState({});
  const [tickerItems, setTickerItems] = useState([]);
  const [geoEvents, setGeoEvents] = useState([]);
  const [friendShoringPoints, setFriendShoringPoints] = useState([]);
  const [friendShoringLoading, setFriendShoringLoading] = useState(true);
  const [countryAiInsights, setCountryAiInsights] = useState({});
  const [selectedChokePointId, setSelectedChokePointId] = useState("malacca");
  const disruptedArcIds = useMemo(() => {
    const ids = new Set();
    if (chinaBan) ids.add("ind-chn");
    if (malaccaBlockade) ids.add("ind-chn");
    if (southChinaSeaPct > 0) {
      ids.add("ind-chn");
      ids.add("ind-idn");
    }
    return ids;
  }, [chinaBan, malaccaBlockade, southChinaSeaPct]);
  const disruptedPathIds = useMemo(() => {
    const ids = new Set();
    if (hormuzBlockade) ids.add("hormuz-india");
    if (chinaBan) ids.add("india-malacca-china");
    if (malaccaBlockade) ids.add("india-malacca-china");
    if (southChinaSeaPct > 0) ids.add("india-malacca-china");
    return ids;
  }, [chinaBan, malaccaBlockade, hormuzBlockade, southChinaSeaPct]);

  const sanitizeInsightText = (text) => {
    const raw = String(text || "")
      .replace(/[–—−]/g, "-")
      .replace(/\*\*/g, "")
      .replace(/^\s{0,3}#{1,6}\s*/gm, "")
      .replace(/\s+/g, " ")
      .trim();
    if (!raw) return "";
    const stripped = raw
      .replace(/^Based on the provided events,?\s*/i, "")
      .replace(/^HEADLINE:\s*/i, "")
      .replace(/^SUMMARY:\s*/i, "")
      .replace(/\b(HEADLINE|SUMMARY|RISK OUTLOOK|ANALYSIS|RECOMMENDED ACTION(?: FOR INDIA FRIEND[-\s]?SHORING)?)\s*:?/gi, " ")
      .replace(/\s+/g, " ")
      .replace(/\s+([.,;:])/g, "$1")
      .trim();
    return stripped.charAt(0).toUpperCase() + stripped.slice(1);
  };

  const parseInsightSections = (text) => {
    const normalizeInsightLabel = (label) => {
      const normalized = String(label || "").replace(":", "").replace(/\s+/g, " ").trim().toUpperCase();
      if (!normalized) return "";
      if (normalized.includes("RECOMMENDED ACTION")) return "Recommended Action";
      if (normalized === "HEADLINE") return "Headline";
      if (normalized === "SUMMARY") return "Summary";
      if (normalized === "RISK OUTLOOK") return "Risk Outlook";
      if (normalized === "ANALYSIS") return "Analysis";
      return label;
    };

    const cleaned = String(text || "")
      .replace(/[–—−]/g, "-")
      .replace(/\*\*/g, "")
      .replace(/\r/g, "")
      .replace(/^\s{0,3}#{1,6}\s*/gm, "")
      .trim();
    if (!cleaned) return [];
    const markerRegex = /(?:^|\s)(HEADLINE|SUMMARY|RISK OUTLOOK|ANALYSIS|RECOMMENDED ACTION(?:\s+FOR\s+INDIA\s+FRIEND[-\s]?SHORING)?)\s*:?/gi;
    const matches = Array.from(cleaned.matchAll(markerRegex));
    if (matches.length === 0) {
      return [{ label: "Insight", body: sanitizeInsightText(cleaned) }];
    }
    const sections = matches.map((match, idx) => {
      const rawLabel = match[1] || "";
      const bodyStart = (match.index ?? 0) + match[0].length;
      const nextIndex = idx < matches.length - 1 ? (matches[idx + 1].index ?? cleaned.length) : cleaned.length;
      const rawBody = cleaned.slice(bodyStart, nextIndex);
      return {
        label: normalizeInsightLabel(rawLabel),
        body: sanitizeInsightText(rawBody),
      };
    }).filter((section) => section.label && section.body);

    if (sections.length === 0) {
      return [{ label: "Insight", body: sanitizeInsightText(cleaned) }];
    }
    const sectionOrder = {
      Headline: 1,
      Summary: 2,
      "Risk Outlook": 3,
      Analysis: 4,
      "Recommended Action": 5,
    };
    sections.sort((a, b) => (sectionOrder[a.label] || 99) - (sectionOrder[b.label] || 99));
    return sections;
  };

  useEffect(() => {
    const container = globeContainerRef.current;
    if (!container) return;
    const updateSize = () => {
      if (container) setGlobeSize({ width: container.clientWidth, height: container.clientHeight });
    };
    updateSize();
    const ro = new ResizeObserver(updateSize);
    ro.observe(container);
    return () => ro.disconnect();
  }, []);

  // Fetch matrix points from PostgreSQL-backed API by selected mineral.
  useEffect(() => {
    let mounted = true;
    async function fetchMatrix() {
      setFriendShoringLoading(true);
      try {
        let res = await getFriendShoringMatrix({ mineral: selectedMineral });
        if ((!res?.success || !Array.isArray(res?.data) || res.data.length === 0) && selectedMineral) {
          res = await getFriendShoringMatrix();
        }
        if (!mounted || !res?.success || !Array.isArray(res?.data) || res.data.length === 0) {
          if (mounted) setFriendShoringPoints([]);
          return;
        }
        const mapped = res.data
          .filter((r) => r?.countryIso3 && r?.countryName)
          .map((r) => ({
            id: r.countryIso3,
            name: r.countryName,
            keyMineral: r.mineral || selectedMineral || "N/A",
            nickname: r.nickname || null,
            alignment: Number(r.alignment ?? 0),
            dependence: Number(r.dependence ?? 0),
            coercionRisk: r.coercionRisk || "Unknown",
          }));
        if (mapped.length > 0) {
          setFriendShoringPoints(mapped);
          setSelectedCountry((prev) => (prev && mapped.some((p) => p.id === prev) ? prev : mapped[0].id));
        } else {
          setFriendShoringPoints([]);
        }
      } catch (e) {
        if (mounted) setFriendShoringPoints([]);
      } finally {
        if (mounted) setFriendShoringLoading(false);
      }
    }
    fetchMatrix();
    return () => {
      mounted = false;
    };
  }, [selectedMineral]);

  // Fetch stored country intelligence from DB-backed API and cache it.
  useEffect(() => {
    let mounted = true;

    const country = friendShoringPoints.find((p) => p.id === selectedCountry);
    if (!country) return;
    const cacheKey = `${country.id}:${selectedMineral}`;
    if (countryAiInsights[cacheKey]) return;

    async function fetchCountryInsight() {
      try {
        let dbRes = await getCountryIntelligence(country.id, { minimal: false, mineral: selectedMineral });
        if ((!dbRes?.success || !dbRes?.data) && selectedMineral) {
          dbRes = await getCountryIntelligence(country.id, { minimal: false });
        }
        if (mounted && dbRes?.success && dbRes?.data) {
          const summary =
            (dbRes.data.summaryFull || "").trim() ||
            (dbRes.data.summary || "").trim() ||
            (dbRes.data.summaryShort || "").trim();
          const recommendedAction = sanitizeInsightText(dbRes.data.recommendedAction);
          setCountryAiInsights((prev) => ({
            ...prev,
            [cacheKey]: { summary, recommendedAction },
          }));
        }
      } catch (e) {
        // leave empty when snapshot is unavailable
      }
    }

    fetchCountryInsight();
    return () => {
      mounted = false;
    };
  }, [selectedCountry, countryAiInsights, friendShoringPoints, selectedMineral]);

 

  // Mount three-globe (WebGL) into the container (does not require HTTPS/secure context).
  useEffect(() => {
    const container = globeFallbackRef.current;
    if (!container) return;

    let globeInstance = null;
    let cancelled = false;

    (async () => {
      try {
        const { default: GlobeFactory } = await import("globe.gl");
        if (cancelled) return;
        // ensure container fills available area and center canvas
        container.innerHTML = "";
        container.style.display = "flex";
        container.style.justifyContent = "center";
        container.style.alignItems = "center";
        container.style.height = "100%";
        container.style.background = isDarkMode ? "#021014" : "#ffffff";
        const width = container.clientWidth || globeSize.width;
        const height = container.clientHeight || globeSize.height;
        globeInstance = GlobeFactory()(container)
          .width(width)
          .height(height)
          .backgroundColor(isDarkMode ? "#000000" : "#ffffff")
          .globeImageUrl(
            isDarkMode
              ? "https://unpkg.com/three-globe/example/img/earth-night.jpg"
              : "https://unpkg.com/three-globe/example/img/earth-blue-marble.jpg"
          )
          .arcsData(TRADE_ARCS)
          .arcStartLat((d) => d.startLat)
          .arcStartLng((d) => d.startLng)
          .arcEndLat((d) => d.endLat)
          .arcEndLng((d) => d.endLng)
          .arcColor((d) => {
            if (disruptedArcIds.has(d.id)) return "#fb7185";
            return d.type === "friend" ? "rgb(3, 57, 108)" : d.type === "risk" ? "#fb7185" : "rgb(3, 57, 108)";
          })
          .arcDashLength((d) => (d.type === "risk" ? 0.08 : 0.2))
          .arcDashGap((d) => (d.type === "risk" ? 0.04 : 0.1))
          .arcDashAnimateTime((d) => (d.type === "risk" ? 1500 : 3500))
          .pathsData(TRADE_PATHS)
          .pathPoints("coords")
          .pathPointLat((p) => p[1])
          .pathPointLng((p) => p[0])
          .pathColor((d) => {
            if (disruptedPathIds.has(d.id)) return "#fb7185";
            return d.type === "friend" ? "#22d3ee" : d.type === "risk" ? "#fb7185" : "#bae6fd";
          })
          .pathStroke(0.7)
          .pathDashLength((d) => (d.type === "risk" ? 0.1 : 0.04))
          .pathDashGap((d) => (d.type === "risk" ? 0.06 : 0.03))
          .pathDashAnimateTime((d) => (d.type === "risk" ? 1800 : 2600))
          .pathLabel((d) => `<div style="padding:6px;color:#cbd5e1;font-size:11px">${d.label}</div>`)
          .pointsData(CHOKE_POINTS)
          .pointLat("lat")
          .pointLng("lng")
          .pointColor((d) => (d?.id === selectedChokePointId ? "#fbbf24" : "#e2e8f0"))
          .pointAltitude(0.01)
          .pointRadius((d) => (d?.id === selectedChokePointId ? 0.58 : 0.48))
          .pointLabel((d) => chokePointLabel(d))
          .onPointClick((point) => {
            if (!point?.id) return;
            setSelectedChokePointId(point.id);
          });

        // initial POV
        try {
          globeInstance.pointOfView({ lat: 20, lng: 90, altitude: 2 }, 0);
        } catch (e) {
          // ignore
        }
        // make canvas responsive if present
        try {
          const canvas = container.querySelector("canvas");
          if (canvas) {
            canvas.style.maxWidth = "100%";
            canvas.style.height = "100%";
            canvas.style.display = "block";
            canvas.style.margin = "0 auto";
          }
        } catch (e) {
          // ignore
        }
      } catch (e) {
        // if module not found or mount failed, leave static fallback intact
        console.warn("three-globe mount failed:", e);
      }
    })();

    return () => {
      cancelled = true;
      if (globeInstance) {
        try {
          // attempt graceful disposal
          if (globeInstance.renderer && typeof globeInstance.renderer.dispose === "function") globeInstance.renderer.dispose();
          if (globeInstance.controls && typeof globeInstance.controls.dispose === "function") globeInstance.controls.dispose();
        } catch (e) {
          // ignore
        }
        container.innerHTML = "";
        globeInstance = null;
      }
    };
  }, [globeSize, disruptedArcIds, disruptedPathIds, isDarkMode, selectedChokePointId]);

  // Keep page colors in sync with app-level dark/light mode class.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    const syncTheme = () => setIsDarkMode(root.classList.contains("dark"));
    syncTheme();
    const observer = new MutationObserver(syncTheme);
    observer.observe(root, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  // Fetch pricing for selected minerals from pricing_data_lcn via API
  useEffect(() => {
    let mounted = true;
    const minerals = ["Lithium", "Cobalt", "Nickel"];

    async function fetchPrices() {
      try {
        const results = await Promise.all(
          minerals.map((m) => getComprehensivePricingData(m, "All").catch(() => null))
        );
        if (!mounted) return;
        const byMineral = {};
        results.forEach((res, idx) => {
          const minName = minerals[idx];
          if (!res || !Array.isArray(res.pricingData) || res.pricingData.length === 0) {
            byMineral[minName] = null;
            return;
          }
          // find latest valid pricing record by date
          const valid = res.pricingData
            .filter((r) => r && r.pricing_date && r.value != null && !isNaN(Number(r.value)))
            .map((r) => ({ ...r, _d: new Date(r.pricing_date) }))
            .filter((r) => !isNaN(r._d.getTime()));
          if (valid.length === 0) {
            byMineral[minName] = null;
            return;
          }
          valid.sort((a, b) => a._d - b._d);
          const latest = valid[valid.length - 1];
          // Prefer latest full/partial calendar year so chart axis starts Jan->Dec.
          const latestYear = valid[valid.length - 1]._d.getFullYear();
          const latestYearRows = valid.filter((r) => r._d.getFullYear() === latestYear);
          const monthToValue = new Map();
          latestYearRows.forEach((r) => {
            monthToValue.set(r._d.getMonth(), Number(r.value));
          });
          const monthLabels = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
          const finalSeries = monthLabels
            .map((label, monthIdx) => {
              const value = monthToValue.get(monthIdx);
              if (value == null || Number.isNaN(value)) return null;
              return { t: label, v: value };
            })
            .filter(Boolean);
          byMineral[minName] = {
            value: Number(latest.value),
            currency: latest.currency || "USD/T",
            date: latest.pricing_date,
            series: finalSeries,
          };
        });
        setMineralPrices(byMineral);
      } catch (e) {
        // ignore silently
      }
    }

    fetchPrices();
    return () => {
      mounted = false;
    };
  }, []);

  // Keep top ticker events aligned with Geoeconomic AI page events.
  useEffect(() => {
    let mounted = true;

    const getDisplayHeadline = (ev) =>
      ev?.enrichment?.headline_en || ev?.headline || ev?.title || "";

    async function fetchTopEvents() {
      try {
        const res = await getEnrichedEvents(25);
        if (!mounted || !res?.success || !Array.isArray(res.events)) return;
        setGeoEvents(res.events);

        const latestThree = res.events
          .slice()
          .sort((a, b) => new Date(b?.timestamp || 0) - new Date(a?.timestamp || 0))
          .map((ev) => getDisplayHeadline(ev))
          .filter(Boolean)
          .slice(0, 3);

        setTickerItems(latestThree.slice(0, 4));
      } catch (e) {
        // leave ticker empty
      }
    }

    fetchTopEvents();
    return () => {
      mounted = false;
    };
  }, []);

  const selectedChokePoint = CHOKE_POINTS.find((p) => p.id === selectedChokePointId) || null;

  // Mock inventory / inflow for Reserve Gauge
  const PROJECT_VAULT_TONS = 12000;
  const DAILY_CONSUMPTION_TONS = 300;
  const DAILY_INFLOW_TONS = 500;

  // Shock Factor: China Ban=0.95, Malacca=0.8, Hormuz=0.6, South China Sea=slider×0.8
  const shockFactor = useMemo(() => {
    let sf = 0;
    if (chinaBan) sf = Math.max(sf, 0.95); // China Graphite Export Ban: near-total
    if (malaccaBlockade) sf = Math.max(sf, 0.8); // Malacca Strait: 80% blockage East Asian flow
    if (hormuzBlockade) sf = Math.max(sf, 0.6); // Strait of Hormuz: elevated disruption to westward corridors
    sf = Math.max(sf, (southChinaSeaPct / 100) * 0.8); // South China Sea: 0–80% by slider
    return Math.min(sf, 0.95);
  }, [chinaBan, malaccaBlockade, hormuzBlockade, southChinaSeaPct]);

  // Baseline (no-shock) days remaining for comparison
  const baselineDaysRemaining = useMemo(() => {
    const baseInflow = DAILY_INFLOW_TONS;
    const baseBurn = DAILY_CONSUMPTION_TONS - baseInflow;
    if (baseBurn <= 0) return 999;
    return Math.max(0, Math.round(PROJECT_VAULT_TONS / baseBurn));
  }, []);

  // Days Remaining = Inventory / (Daily Consumption - (Daily Inflow × (1 - Shock Factor)))
  const daysRemaining = useMemo(() => {
    const effectiveInflow = DAILY_INFLOW_TONS * (1 - shockFactor);
    const burnRate = DAILY_CONSUMPTION_TONS - effectiveInflow;
    if (burnRate <= 0) return 999; // sustainment
    return Math.max(0, Math.round(PROJECT_VAULT_TONS / burnRate));
  }, [shockFactor]);

  const shockDelta = baselineDaysRemaining - daysRemaining;

  const selectedCountryData = friendShoringPoints.find((p) => p.id === selectedCountry) || friendShoringPoints[0] || null;
  const selectedCountryAi = selectedCountryData ? (countryAiInsights[`${selectedCountryData.id}:${selectedMineral}`] || null) : null;
  const selectedCountryAiInsight = selectedCountryAi?.summary || "";
  const selectedCountryRecommendedAction = selectedCountryAi?.recommendedAction || "";
  const selectedCountryInsightSections = useMemo(
    () => parseInsightSections(selectedCountryAiInsight),
    [selectedCountryAiInsight]
  );
  const parsedRecommendedAction = useMemo(() => {
    const sec = selectedCountryInsightSections.find((s) => s.label.toLowerCase().startsWith("recommended action"));
    return sec?.body || "";
  }, [selectedCountryInsightSections]);
  const latestEventsByChokepoint = useMemo(() => {
    const selectedName = (selectedChokePoint?.name || "").toLowerCase();
    const keywordMap = {
      malacca: ["malacca", "south china sea", "singapore strait", "indo-pacific", "asean shipping", "straits shipping", "maritime southeast asia"],
      suez: ["suez", "red sea", "egypt canal", "bab el-mandeb", "mediterranean", "canal transit", "east mediterranean"],
      hormuz: [
        "hormuz",
        "strait of hormuz",
        "persian gulf",
        "gulf of oman",
        "gulf shipping",
        "middle east shipping",
        "iran",
        "tehran",
        "usa-iran",
        "us-iran",
        "iranian",
        "tanker",
        "maritime security",
      ],
    };
    const selectedKeywords = keywordMap[selectedChokePointId] || [];
    const countrySignals = {
      malacca: ["singapore", "malaysia", "indonesia", "thailand"],
      suez: ["egypt", "yemen", "israel"],
      hormuz: ["iran", "united states", "usa", "oman", "saudi arabia", "uae", "qatar", "bahrain", "kuwait"],
    };
    const chokepointAliases = {
      malacca: ["malacca", "strait of malacca", "singapore strait"],
      suez: ["suez", "suez canal", "red sea", "bab el-mandeb"],
      hormuz: ["hormuz", "strait of hormuz", "persian gulf", "gulf of oman"],
    };
    const aliasList = chokepointAliases[selectedChokePointId] || [];
    const toTs = (ev) => new Date(ev?.timestamp || ev?.created_at || 0).getTime() || 0;

    const scoreEvent = (ev) => {
      const countries = Array.isArray(ev?.entities?.countries) ? ev.entities.countries : [];
      const sectors = Array.isArray(ev?.enrichment?.related_sectors) ? ev.enrichment.related_sectors : [];
      const eventType = String(ev?.enrichment?.event_type || ev?.event_type || "");
      const cpLeverage = ev?.enrichment?.chokepoint_leverage;
      const cpInvolved = Array.isArray(cpLeverage?.chokepoints_involved) ? cpLeverage.chokepoints_involved : [];
      const cpLeverageBlob = [
        cpLeverage?.relevant ? "relevant" : "",
        cpLeverage?.leverage_mechanism || "",
        cpInvolved.join(" "),
      ].join(" ").toLowerCase();
      const blob = [
        ev?.enrichment?.headline_en || "",
        ev?.headline || "",
        ev?.summary || "",
        ev?.enrichment?.strategic_significance_en || "",
        ev?.enrichment?.strategic_significance || "",
        ev?.enrichment?.economic_leverage_shifts_en || "",
        ev?.enrichment?.economic_leverage_shifts || "",
        countries.join(" "),
        sectors.join(" "),
        eventType,
        cpLeverageBlob,
      ]
        .join(" ")
        .toLowerCase();

      let score = 0;
      if (cpLeverage?.relevant && aliasList.some((a) => cpLeverageBlob.includes(a))) score += 10;
      if (aliasList.some((a) => blob.includes(a))) score += 8;
      if (selectedKeywords.some((k) => blob.includes(k))) score += 6;
      if (selectedName && blob.includes(selectedName)) score += 6;
      if ((countrySignals[selectedChokePointId] || []).some((c) => blob.includes(c))) score += 3;
      if (String(eventType).toLowerCase().includes("chokepoint")) score += 3;
      if (selectedChokePointId === "hormuz" && (blob.includes("iran") || blob.includes("persian gulf"))) score += 3;
      return score;
    };

    const ranked = (geoEvents || [])
      .map((ev) => ({ ev, score: scoreEvent(ev) }))
      .filter((x) => x.score > 0)
      .sort((a, b) => (b.score - a.score) || (toTs(b.ev) - toTs(a.ev)));

    let result = ranked.slice(0, 4).map((x) => x.ev);
    if (result.length === 0) {
      // Relaxed fallback: still chokepoint-adjacent by country signals, ordered by recency.
      result = (geoEvents || [])
        .filter((ev) => {
          const countries = Array.isArray(ev?.entities?.countries) ? ev.entities.countries : [];
          const blob = [ev?.headline || "", ev?.summary || "", countries.join(" ")].join(" ").toLowerCase();
          return (countrySignals[selectedChokePointId] || []).some((c) => blob.includes(c));
        })
        .sort((a, b) => toTs(b) - toTs(a))
        .slice(0, 4);
    }

    return result.map((ev, idx) => ({
      id: ev?.event_id || `${selectedChokePointId}-${idx}`,
      headline: ev?.enrichment?.headline_en || ev?.headline || ev?.title || "Untitled event",
      time: ev?.timestamp || ev?.created_at || null,
      source: ev?.data_source || "feed",
      sourceUrl: ev?.source_url || ev?.enrichment?.source_url || ev?.url || "",
    }));
  }, [geoEvents, selectedChokePointId, selectedChokePoint]);

  // Use only pricing_data_lcn series for charting; no static seeded series.
  const esgMultiplier = priceInESG ? 1.15 : 1;

  const selectedSeries = useMemo(() => {
    const series = mineralPrices?.[selectedMineral]?.series;
    if (Array.isArray(series) && series.length >= 2) return series;
    return [];
  }, [mineralPrices, selectedMineral]);

  // Build base and conflict series from the chosen selectedSeries
  const hasChartData = selectedSeries.length >= 2;
  const chartBase = selectedSeries.map((p) => ({ ...p, v: Math.round(p.v * esgMultiplier) }));
  const riskConflictSeries = selectedSeries.map((p) => ({ ...p, v: Math.round(p.v * (1 + GPR * LITHIUM_BETA)) }));
  const chartConflict = riskConflictSeries.map((p) => ({ ...p, v: Math.round(p.v * esgMultiplier) }));
  const marketChartData = chartBase.map((point, idx) => ({
    period: point.t || `P${idx + 1}`,
    base: point.v,
    conflict: chartConflict[idx]?.v ?? point.v,
  }));
  // premium should reflect any ESG uplift applied to both base and conflict scenarios
  const premiumAmount = (chartConflict[Math.floor(chartConflict.length/2)]?.v - chartBase[Math.floor(chartBase.length/2)]?.v) ?? 0;
  const pageTheme = isDarkMode
    ? {
        pageBg: "#0b0f14",
        text: "#cbd5e1",
        muted: "#94a3b8",
        subtle: "#64748b",
        accent: "#7dd3fc",
        border: "rgba(255,255,255,0.03)",
        headerBg: "#071016",
        sectionBg: "#07121a",
        cardBg: "#061217",
        controlBg: "#041015",
        globeBg: "#021014",
        insetBg: "#08131a",
        axis: "#475569",
        grid: "rgba(255,255,255,0.04)",
        matrixLabel: "#94a3b8",
        strongText: "#cbd5e1",
      }
    : {
        pageBg: "#f8fafc",
        text: "#0f172a",
        muted: "#334155",
        subtle: "#64748b",
        accent: "#0369a1",
        border: "rgba(15,23,42,0.08)",
        headerBg: "#e2e8f0",
        sectionBg: "#ffffff",
        cardBg: "#f1f5f9",
        controlBg: "#e2e8f0",
        globeBg: "#ffffff",
        insetBg: "#f8fafc",
        axis: "#94a3b8",
        grid: "rgba(15,23,42,0.08)",
        matrixLabel: "#334155",
        strongText: "#0f172a",
      };

  return (
    <div style={{ minHeight: "100vh", background: pageTheme.pageBg, color: pageTheme.text, fontFamily: "Inter, Segoe UI, system-ui, sans-serif", fontSize: 14 }}>
      {/* Ticker */}
      <header style={{ background: pageTheme.headerBg, padding: "8px 12px", borderBottom: `1px solid ${pageTheme.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <h2 style={{ margin: 0, color: pageTheme.accent }}>Latest News</h2>
          <div style={{ flex: 1, overflow: "hidden" }}>
            {tickerItems.length > 0 ? (
              <div style={{ whiteSpace: "nowrap", display: "inline-block", animation: "ticker 20s linear infinite" }}>
                {tickerItems.map((t, i) => (
                  <React.Fragment key={i}>
                    <span style={{ color: pageTheme.muted, paddingLeft: i === 0 ? 0 : 32, paddingRight: 32 }}>
                      [{t}]
                    </span>
                    {i < tickerItems.length - 1 && (
                      <span style={{ color: "rgb(3, 57, 108)", opacity: 0.7, paddingLeft: 0, paddingRight: 0, fontSize: "0.6em" }} aria-hidden="true">
                        ◆
                      </span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            ) : (
              <div style={{ color: pageTheme.muted, fontSize: 12 }}>No live news events available.</div>
            )}
          </div>
        </div>
      </header>

      <section style={{ padding: "20px 20px 8px" }}>
        <h1 style={{ margin: 0, fontSize: 32, fontWeight: 700, color: pageTheme.strongText }}>Global Resource Sentinel</h1>
        <p style={{ margin: "8px 0 0", color: pageTheme.muted, lineHeight: 1.5 }}>
          This page provides a live view of critical mineral supply-chain risk by combining geopolitical signals, chokepoint disruption scenarios, partner alignment, and price intelligence to support faster sourcing and resilience decisions.
        </p>
      </section>

      <main style={{ display: "grid", gridTemplateColumns: "320px 1fr 360px", gap: 16, padding: 20 }}>
        {/* Left Sidebar */}
        <aside style={{ background: pageTheme.sectionBg, padding: 16, borderRadius: 8, height: "calc(100vh - 140px)", overflowY: "auto" }}>
          {/* Friend-Shoring Matrix (SVG scatter) */}
          <div style={{ marginTop: 12 }}>
            <h3 style={{ color: "rgb(3, 57, 108)", marginTop: 0, fontSize: 16 }}>Friend-Shoring Matrix</h3>
            <p style={{ margin: "4px 0 2px", color: pageTheme.muted, fontSize: 12, lineHeight: 1.4 }}>
              The Friend Shoring Matrix maps countries by strategic alignment and import dependence to highlight resilient partners.
            </p>
            <p style={{ margin: "0 0 10px", color: pageTheme.muted, fontSize: 12, lineHeight: 1.4 }}>
              Use the top-left zone as low-risk sourcing options and monitor bottom-right positions for concentration and geopolitical risk.
            </p>
            <div style={{ background: pageTheme.insetBg, padding: 10, borderRadius: 6, border: `1px solid ${pageTheme.border}` }}>
              <svg viewBox="0 0 220 220" width="100%" style={{ display: "block" }}>
                {/* Plot area: x 35-210, y 10-180 (X=Dependence left→right, Y=Alignment bottom→top) */}
                <defs>
                  <clipPath id="matrixClip">
                    <rect x="35" y="10" width="175" height="170" />
                  </clipPath>
                </defs>
                {/* Y-axis (Alignment) - vertical line on left */}
                <line x1="35" y1="10" x2="35" y2="180" stroke={pageTheme.axis} strokeWidth="1" />
                {/* X-axis (Dependence) - horizontal line on bottom */}
                <line x1="35" y1="180" x2="210" y2="180" stroke={pageTheme.axis} strokeWidth="1" />
                {/* Y-axis label (Alignment) - left side, rotated or at top */}
                <text x="12" y="95" fill={pageTheme.subtle} fontSize="10" textAnchor="middle" transform="rotate(-90 12 95)">Alignment Score</text>
                {/* X-axis label (Dependence) - bottom center */}
                <text x="122" y="205" fill={pageTheme.subtle} fontSize="10" textAnchor="middle">Dependence (%)</text>
                {/* Y-axis ticks: 0, 50, 100 */}
                {[0, 50, 100].map((v, i) => {
                  const y = 180 - (v / 100) * 170;
                  return (
                    <g key={`y-${v}`}>
                      <line x1="35" y1={y} x2="38" y2={y} stroke={pageTheme.axis} strokeWidth="1" />
                      <text x="32" y={y + 3} fill={pageTheme.subtle} fontSize="9" textAnchor="end">{v}</text>
                    </g>
                  );
                })}
                {/* X-axis ticks: 0, 50, 100 */}
                {[0, 50, 100].map((v, i) => {
                  const x = 35 + (v / 100) * 175;
                  return (
                    <g key={`x-${v}`}>
                      <line x1={x} y1="180" x2={x} y2="183" stroke={pageTheme.axis} strokeWidth="1" />
                      <text x={x} y="192" fill={pageTheme.subtle} fontSize="9" textAnchor="middle">{v}</text>
                    </g>
                  );
                })}
                {/* Grid lines */}
                {[0, 50, 100].map((v, i) => (
                  <g key={`grid-${v}`}>
                    <line x1="35" y1={180 - (v / 100) * 170} x2="210" y2={180 - (v / 100) * 170} stroke={pageTheme.grid} strokeWidth="1" />
                    <line x1={35 + (v / 100) * 175} y1="10" x2={35 + (v / 100) * 175} y2="180" stroke={pageTheme.grid} strokeWidth="1" />
                  </g>
                ))}
                {/* Plot area clip */}
                <g clipPath="url(#matrixClip)">
                  {/* Safe: Alignment>70, Dependence<30 → top-left */}
                  {/* alignment > 70 maps to y = 180 - (70/100)*170 = 61, so rect from y=10 to y=61 => height = 51 */}
                  <rect x="35" y="10" width="87.5" height="85.5" fill="rgba(52,211,153,0.06)" />
                  {/* Critical: Alignment<40, Dependence>50 → bottom-right */}
                  <rect x="122.5" y="95" width="87.5" height="85.5" fill="rgba(251,113,133,0.06)" />
                  {friendShoringPoints.map((p) => {
                    const cx = 35 + (p.dependence / 100) * 175;
                    const cy = 180 - (p.alignment / 100) * 170;
                    const zone = getQuadrantZone(p.alignment, p.dependence);
                    const color = zone === "safe" ? "#34d399" : zone === "critical" ? "#fb7185" : zone === "concern" ? "#fbbf24" : "rgb(3, 57, 108)";
                    return (
                      <g key={p.id} onClick={() => setSelectedCountry(p.id)} style={{ cursor: "pointer" }}>
                        <circle cx={cx} cy={cy} r={zone === "safe" ? 6.5 : 5.5} fill={color} stroke={isDarkMode ? "rgba(255,255,255,0.14)" : "rgba(15,23,42,0.16)"} />
                      </g>
                    );
                  })}
                </g>
                {/* Labels outside clip (so they don't get cut) */}
                {friendShoringPoints.map((p) => {
                  const cx = 35 + (p.dependence / 100) * 175;
                  const cy = 180 - (p.alignment / 100) * 170;
                  return (
                    <text key={`label-${p.id}`} x={Math.min(Math.max(cx + 10, 40), 200)} y={cy + 4} fontSize="9.5" fill={pageTheme.matrixLabel} style={{ pointerEvents: "none" }}>{p.name}</text>
                  );
                })}
              </svg>
              {!friendShoringLoading && friendShoringPoints.length === 0 && (
                <div style={{ marginTop: 8, fontSize: 12, color: pageTheme.muted }}>
                  No friend-shoring matrix data available from API.
                </div>
              )}
            </div>
          </div>

          {/* Shock Simulator - Burn Rate: Inventory / (Consumption - Inflow×(1-Shock)) */}
          <div style={{ marginTop: 18 }}>
            <h3 style={{ color: "rgb(3, 57, 108)", marginTop: 0 }}>Shock Simulator</h3>
            <p style={{ margin: "4px 0 10px", color: pageTheme.muted, fontSize: 12, lineHeight: 1.4 }}>
              Toggle disruption scenarios to estimate supply stress and the resulting reserve burn-down trajectory.
            </p>
            
            <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input type="checkbox" checked={chinaBan} onChange={(e) => setChinaBan(e.target.checked)} />
              <span style={{ fontSize: 13 }}>China Export Ban</span>
              <span style={{ fontSize: 11, color: chinaBan ? "#fb7185" : "#64748b" }}>(S=0.95)</span>
            </label>
            <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
              <input type="checkbox" checked={malaccaBlockade} onChange={(e) => setMalaccaBlockade(e.target.checked)} />
              <span style={{ fontSize: 13 }}>Malacca Strait Blockade</span>
              <span style={{ fontSize: 11, color: malaccaBlockade ? "#fb7185" : "#64748b" }}>(S=0.8)</span>
            </label>
            <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
              <input type="checkbox" checked={hormuzBlockade} onChange={(e) => setHormuzBlockade(e.target.checked)} />
              <span style={{ fontSize: 13 }}>Strait of Hormuz Chokepoint Block</span>
              <span style={{ fontSize: 11, color: hormuzBlockade ? "#fb7185" : "#64748b" }}>(S=0.6)</span>
            </label>
            <div style={{ marginTop: 10 }}>
              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={southChinaSeaBlockade}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setSouthChinaSeaBlockade(checked);
                    if (!checked) setSouthChinaSeaPct(0);
                  }}
                />
                <span style={{ fontSize: 13 }}>South China Sea Blockade</span>
              </label>
              {southChinaSeaBlockade && (
                <>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={southChinaSeaPct}
                    onChange={(e) => setSouthChinaSeaPct(Number(e.target.value))}
                    style={{ width: "100%", marginTop: 8 }}
                  />
                  <div style={{ fontSize: 12, color: pageTheme.strongText }}>{southChinaSeaPct}% intensity</div>
                </>
              )}
            </div>

            <div style={{ marginTop: 14, background: pageTheme.cardBg, padding: 10, borderRadius: 6 }}>
              <div style={{ fontSize: 12, color: pageTheme.muted }}>Reserve Gauge</div>
              <div style={{ marginTop: 3, fontSize: 11, color: pageTheme.subtle }}>Live estimate of how long reserves can sustain current disruption levels.</div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
                <svg width="80" height="80" viewBox="0 0 36 36">
                  <path
                    d="M18 2a16 16 0 1 0 16 16A16 16 0 0 0 18 2"
                    fill="none"
                    stroke="rgba(3, 57, 108, 0.22)"
                    strokeWidth="2"
                  />
                  <path
                    d="M18 2a16 16 0 1 0 16 16A16 16 0 0 0 18 2"
                    fill="none"
                    stroke={daysRemaining < 30 ? "#fb7185" : daysRemaining < 90 ? "#fbbf24" : "rgb(3, 57, 108)"}
                    strokeWidth="2"
                    strokeDasharray={`${(daysRemaining / 180) * 100} 100`}
                    transform="rotate(-90 18 18)"
                    style={{ transition: "stroke-dasharray 700ms ease, stroke 300ms ease" }}
                  />
                  <text x="18" y="20" fontSize="4" textAnchor="middle" fill={pageTheme.strongText}>{daysRemaining}d</text>
                </svg>
                <div>
                  <div style={{ fontSize: 13, color: pageTheme.strongText }}>Project Vault</div>
                  <div style={{ fontSize: 12, color: pageTheme.muted }}>{PROJECT_VAULT_TONS} t reserves</div>
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* Center - 3D Globe with Trade Corridors */}
        <section style={{ position: "relative", borderRadius: 8, overflow: "hidden", background: pageTheme.globeBg, minHeight: "calc(100vh - 140px)", height: "calc(100vh - 140px)" }}>
          <div ref={globeContainerRef} style={{ position: "absolute", inset: 0, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <div style={{ width: "100%", height: "100%", position: "relative" }}>
              <div ref={globeFallbackRef} style={{ width: "100%", height: "100%" }} />
            </div>
          </div>
          {/* overlay when severe (visual turn red) */}
          {shockFactor > 0.6 && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(251,113,133,0.12)",
                pointerEvents: "none",
                transition: "background 300ms ease",
                mixBlendMode: "overlay",
              }}
            />
          )}
        </section>

        {/* Right Sidebar - Country Intelligence */}
        <aside style={{ background: pageTheme.sectionBg, padding: 16, borderRadius: 8, height: "calc(100vh - 140px)", display: "flex", flexDirection: "column", gap: 14, overflow: "hidden" }}>
          <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <h3 style={{ color: "rgb(3, 57, 108)", margin: 0 }}>Country Intelligence</h3>
              <div style={{ fontSize: 12, color: pageTheme.muted }}>
                Score: {selectedCountryData?.alignment != null ? `${selectedCountryData.alignment}/100` : "N/A"}
              </div>
            </div>
            <div style={{ marginTop: 4, fontSize: 11, color: pageTheme.subtle, lineHeight: 1.4 }}>
              Strategic profile and risk context by country. Select an available country in the Friend-Shoring Matrix to view specific Country Intelligence.
            </div>

            <div style={{
              marginTop: 12,
              background: pageTheme.cardBg,
              padding: 12,
              borderRadius: 8,
              borderLeft:
                selectedCountryData && getQuadrantZone(selectedCountryData.alignment, selectedCountryData.dependence) === "critical"
                  ? "3px solid #fb7185"
                  : "3px solid transparent",
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
            }}>
            <div style={{ fontSize: 14, color: pageTheme.strongText, fontWeight: 600 }}>{selectedCountryData?.name || "No country selected"}</div>
            <div style={{ marginTop: 6, color: pageTheme.muted, fontSize: 13 }}>
              {selectedCountryData?.keyMineral || "N/A"}
              {selectedCountryData?.nickname && (
                <span style={{ marginLeft: 6, color: "rgb(3, 57, 108)" }}>— {selectedCountryData.nickname}</span>
              )}
            </div>
            <div style={{ marginTop: 4, fontSize: 11, color: "#64748b" }}>{selectedCountryData?.coercionRisk || "N/A"}</div>
            <div style={{ marginTop: 10, fontSize: 13, color: pageTheme.strongText }}>
              Strategic Insight:
              <div style={{ marginTop: 6, color: pageTheme.muted, fontSize: 13, lineHeight: 1.45 }}>
                {selectedCountryInsightSections.length > 0 &&
                  selectedCountryInsightSections
                    .filter((section) => !section.label.toLowerCase().startsWith("recommended action"))
                    .map((section, idx) => (
                      <div key={`${section.label}-${idx}`} style={{ marginBottom: 8 }}>
                        <span style={{ fontWeight: 700, color: pageTheme.strongText }}>{section.label}:</span>{" "}
                        <span>{section.body}</span>
                      </div>
                    ))}
                {selectedCountryData && !selectedCountryAiInsight && "No intelligence snapshot available yet."}
                {!selectedCountryData && "No country intelligence data available."}
              </div>
            </div>

            {(parsedRecommendedAction || selectedCountryRecommendedAction) && (
              <div style={{ marginTop: 10, fontSize: 12, color: pageTheme.muted }}>
                <span style={{ fontWeight: 700, color: pageTheme.strongText }}>Recommended Action:</span>{" "}
                <span style={{ color: pageTheme.strongText }}>{parsedRecommendedAction || selectedCountryRecommendedAction}</span>
              </div>
            )}

            {selectedCountryData && getQuadrantZone(selectedCountryData.alignment, selectedCountryData.dependence) === "critical" && (
              <div style={{ marginTop: 10, padding: 8, background: "rgba(251,113,133,0.15)", borderRadius: 6, fontSize: 12, color: "#fb7185" }}>
                ⚠ Action Required: Diversify supply immediately
              </div>
            )}
            </div>
          </div>

          <div style={{ flex: 1, minHeight: 0, background: pageTheme.cardBg, padding: 12, borderRadius: 8, display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <div style={{ fontSize: 14, color: "rgb(3, 57, 108)", fontWeight: 600 }}>Latest Events</div>
              <div style={{ fontSize: 11, color: pageTheme.muted }}>{selectedChokePoint?.name || "Selected Chokepoint"}</div>
            </div>
            <div style={{ marginTop: 4, fontSize: 11, color: pageTheme.subtle }}>
              Select different chokepoints on globe to see latest events.
            </div>
            <div style={{ marginTop: 8, flex: 1, minHeight: 0, overflowY: "auto" }}>
              {latestEventsByChokepoint.length > 0 ? (
                latestEventsByChokepoint.map((ev) => (
                  <div key={ev.id} style={{ padding: "8px 0", borderBottom: `1px solid ${pageTheme.border}` }}>
                    <a
                      href={ev.sourceUrl || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: 12, color: pageTheme.accent, lineHeight: 1.35, textDecoration: "underline" }}
                      onClick={(e) => {
                        if (!ev.sourceUrl) e.preventDefault();
                      }}
                      title={ev.sourceUrl ? "Open original source" : "Source URL unavailable"}
                    >
                      {ev.headline}
                    </a>
                    <div style={{ marginTop: 4, fontSize: 10, color: pageTheme.subtle }}>
                      {ev.time ? new Date(ev.time).toLocaleString() : "Recent"} • {String(ev.source).toUpperCase()}
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ fontSize: 12, color: pageTheme.subtle, paddingTop: 6 }}>
                  No recent events available for this chokepoint.
                </div>
              )}
            </div>
          </div>
        </aside>
      </main>

      {/* Bottom Panel - Executive Control Room */}
      <footer style={{ padding: 20, borderTop: `1px solid ${pageTheme.border}`, background: pageTheme.controlBg }}>
        <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
          {/* Left: Reserve Burn Rate (Under Shock) */}
          <div style={{ background: pageTheme.cardBg, padding: 14, borderRadius: 8, width: 280 }}>
            <div style={{ fontSize: 12, color: pageTheme.muted }}>Strategic Reserve Burn Rate</div>
            <div style={{ marginTop: 3, fontSize: 11, color: pageTheme.subtle }}>Tracks projected reserve depletion speed under active shock conditions.</div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
              <svg width="80" height="80" viewBox="0 0 36 36">
                <path d="M18 2a16 16 0 1 0 16 16A16 16 0 0 0 18 2" fill="none" stroke="rgba(3, 57, 108, 0.22)" strokeWidth="2" />
                <path d="M18 2a16 16 0 1 0 16 16A16 16 0 0 0 18 2" fill="none"
                  stroke={daysRemaining < 30 ? "#fb7185" : daysRemaining < 90 ? "#fbbf24" : "rgb(3, 57, 108)"}
                  strokeWidth="2"
                  strokeDasharray={`${Math.min(100, (daysRemaining / 180) * 100)} 100`}
                  transform="rotate(-90 18 18)"
                  style={{ transition: "stroke-dasharray 700ms ease, stroke 300ms ease" }} />
              </svg>
              <div>
                <div style={{ fontSize: 18, color: daysRemaining < 30 ? "#fb7185" : daysRemaining < 90 ? "#fbbf24" : "rgb(3, 57, 108)", fontWeight: 700 }}>
                  {daysRemaining === 999 ? "Sustained" : `${daysRemaining} Days`}
                </div>
                <div style={{ fontSize: 11, color: pageTheme.subtle }}>
                  {shockFactor > 0
                    ? `${Math.round(shockFactor * 100)}% disruption — ${[
                        chinaBan && "China Export Ban",
                        malaccaBlockade && "Malacca Blockade",
                        hormuzBlockade && "Hormuz Block",
                        southChinaSeaPct > 0 && `SCS ${southChinaSeaPct}%`,
                      ].filter(Boolean).join(", ")}`
                    : "No active disruption"}
                </div>
              </div>
            </div>
          </div>

          {/* Middle: Geopolitical Risk Premium (Pricing View) */}
          <div style={{ background: pageTheme.cardBg, padding: 14, borderRadius: 8, flex: 1 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 12, color: "#94a3b8" }}>Geopolitical Risk Premium — Market Prices</div>
                <div style={{ fontSize: 10, color: "#64748b", marginTop: 2 }}>Scenario split: Base Case vs Conflict Scenario</div>
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 11, color: "#94a3b8" }}>
                <span>Price in ESG compliance?</span>
                <input
                  type="checkbox"
                  checked={priceInESG}
                  onChange={(e) => setPriceInESG(e.target.checked)}
                  style={{ accentColor: "rgb(3, 57, 108)" }}
                />
                <span style={{ color: priceInESG ? "rgb(3, 57, 108)" : "#64748b" }}>{priceInESG ? "Yes" : "No"}</span>
              </label>
            </div>

            <div style={{ marginTop: 12, display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
              <div style={{ minWidth: 220 }}>
                <label style={{ display: "block", fontSize: 12, color: "#94a3b8", marginBottom: 6 }}>Select mineral</label>
                <select
                  value={selectedMineral}
                  onChange={(e) => setSelectedMineral(e.target.value)}
                  style={{ width: "100%", padding: "8px 10px", borderRadius: 6, background: pageTheme.controlBg, color: pageTheme.text, border: `1px solid ${pageTheme.border}` }}
                >
                  {["Lithium", "Cobalt", "Nickel"].map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>

              {/* Selected mineral card */}
              <div style={{ background: pageTheme.controlBg, padding: 10, borderRadius: 6, minWidth: 180 }}>
                {(() => {
                  const entry = mineralPrices[selectedMineral];
                  const rawValue = entry?.value ?? null;
                  const unit = entry?.currency || "USD/T";
                  const displayValue = rawValue != null ? Math.round(rawValue * esgMultiplier) : null;
                  const displayDate = entry?.date ? new Date(entry.date) : null;
                  const displayYear = displayDate && !isNaN(displayDate.getTime()) ? displayDate.getFullYear() : null;
                  return (
                    <div>
                      <div style={{ fontSize: 12, color: "#9ca3af" }}>{selectedMineral}</div>
                      <div style={{ fontSize: 16, color: "rgb(3, 57, 108)", fontWeight: 700, marginTop: 6 }}>
                        {displayValue != null ? `$${displayValue.toLocaleString()}` : "N/A"}
                      </div>
                      <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>{unit}{displayYear ? ` • As of ${displayYear}` : ''}</div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Price chart with scenario split and premium shading */}
            <div style={{ marginTop: 12 }}>
              {hasChartData ? (
              <div style={{ marginTop: 6, height: 180 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={marketChartData} margin={{ top: 8, right: 18, left: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "#374151" : "#d1d5db"} />
                    <XAxis dataKey="period" stroke={isDarkMode ? "#9ca3af" : "#6b7280"} fontSize={11} />
                    <YAxis
                      stroke={isDarkMode ? "#9ca3af" : "#6b7280"}
                      fontSize={11}
                      tickFormatter={(v) => `$${Number(v).toLocaleString()}`}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
                        border: `1px solid ${isDarkMode ? "#374151" : "#d1d5db"}`,
                        borderRadius: "0.5rem",
                        color: isDarkMode ? "#ffffff" : "#111827",
                      }}
                      formatter={(value) => [`$${Number(value).toLocaleString()}`, ""]}
                      labelStyle={{ color: isDarkMode ? "#9ca3af" : "#6b7280" }}
                    />
                    <Legend wrapperStyle={{ color: isDarkMode ? "#9ca3af" : "#6b7280", fontSize: 11 }} />
                    <Line type="monotone" dataKey="base" name="Base Case" stroke="rgb(3, 57, 108)" strokeWidth={2.5} dot={false} />
                    <Line type="monotone" dataKey="conflict" name="Conflict Scenario" stroke="#fb7185" strokeWidth={2.2} strokeDasharray="6 4" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              ) : (
                <div style={{ marginTop: 6, fontSize: 12, color: pageTheme.muted }}>
                  No pricing time-series available for selected mineral.
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 8 }}>
                <div style={{ fontSize: 12, color: "#9ca3af" }}>Premium (conflict - base): <span style={{ color: "#fb7185", fontWeight: 700 }}>${premiumAmount.toLocaleString()}</span></div>
              </div>
            </div>
          </div>

          
        </div>
      </footer>

      <style>{`
        @keyframes ticker {
          0% { transform: translateX(0%); }
          100% { transform: translateX(-100%); }
        }
      `}</style>
    </div>
  );
}

